// TechnicalAnalysis.jsx
import { Col, Container, Row, Image, Modal } from 'react-bootstrap';
import '../subScreens.scss';
import React, { useEffect, useRef, useState } from 'react';
import bear from '../../../assets/img/bear.png';
import bearDark from '../../../assets/img/bear-dark.png';
import bull from '../../../assets/img/bull.png';
import bullDark from '../../../assets/img/bull-dark.png';
import bullandbear from '../../../assets/img/bullandbear.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHexagon, faHexagonCheck, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import LineChartTA from './LineChartTA';
import { TechnicalAnalysisServiceApi } from '../../../services/technicalanalysis/api';
import { Configuration } from '../../../services/technicalanalysis/configuration';
import { useSelector } from 'react-redux';
import { APIControl } from '../../../helpers/APIControl';
import DateFormatter from '../../../formatters/DateFormatter';
import moment from 'moment';
import Spinner from '../../spinner';
import ErrorWithReload from '../../charts/ErrorWithReload';
import { useTranslation } from 'react-i18next';

const TechnicalAnalysis = (props) => {
    const { t } = useTranslation();
    const DAILY_FIELD_ID = 7;
    const DAILY_KEYWORDS = ['gunluk', 'gnlk', 'daily'];
    const WEEKLY_KEYWORDS = ['haftalik', 'haftalk', 'weekly'];
    const technicalAnalysisServiceApi = new TechnicalAnalysisServiceApi(
        new Configuration({ basePath: process.env.REACT_APP_TECHNICALANALYSIS_API_BASE_PATH }),
        'undefined',
        APIControl(process.env.REACT_APP_TECHNICALANALYSIS_API_BASE_PATH)
    );

    const dateFormatter = new DateFormatter();
    const [indicators, setIndicators] = useState([]);
    const [activePeriod, setActivePeriod] = useState('1Y');
    const [periods] = useState(['6M', '1Y', '2Y', '3Y', '5Y', 'TÜMÜ']);
    const [chartData, setChartData] = useState([]);
    const [filteredChartData, setFilteredChartData] = useState([]);
    const [isLoadingTable, setIsLoadingTable] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isDaily, setIsDaily] = useState(null);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [selectedIndicator, setSelectedIndicator] = useState(null);
    const token = useSelector((state) => state.auth?.token);
    const latestFetchIdRef = useRef(0);
    const normalizeForCompare = (value) =>
        (value || '')
            .toString()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]/g, '')
            .trim();

    const resolveIsDaily = (selectedBullAndBearItem) => {
        if (selectedBullAndBearItem === null || selectedBullAndBearItem === undefined) {
            return null;
        }

        if (typeof selectedBullAndBearItem === 'string') {
            const normalizedText = normalizeForCompare(selectedBullAndBearItem);
            if (!normalizedText) return null;
            if (DAILY_KEYWORDS.some((keyword) => normalizedText.includes(keyword))) return true;
            if (WEEKLY_KEYWORDS.some((keyword) => normalizedText.includes(keyword))) return false;
            const numericText = Number(normalizedText);
            if (Number.isFinite(numericText)) return numericText === DAILY_FIELD_ID;
            return false;
        }

        if (typeof selectedBullAndBearItem === 'object') {
            const labelText = [
                selectedBullAndBearItem.scoreName,
                selectedBullAndBearItem.name,
                selectedBullAndBearItem.title
            ]
                .filter(Boolean)
                .join(' ');
            const normalizedLabelText = normalizeForCompare(labelText);

            if (DAILY_KEYWORDS.some((keyword) => normalizedLabelText.includes(keyword))) return true;
            if (WEEKLY_KEYWORDS.some((keyword) => normalizedLabelText.includes(keyword))) return false;

            const candidateId = selectedBullAndBearItem.fieldId ?? selectedBullAndBearItem.id;
            const numericId = Number(candidateId);
            if (Number.isFinite(numericId)) return numericId === DAILY_FIELD_ID;
            return false;
        }

        const numericValue = Number(selectedBullAndBearItem);
        if (Number.isFinite(numericValue)) return numericValue === DAILY_FIELD_ID;
        return null;
    };

    useEffect(() => {
        if (props?.entityId && props?.selectedBullAndBearItem !== null) {
            const shouldFetchDaily = resolveIsDaily(props.selectedBullAndBearItem);
            if (shouldFetchDaily === null) {
                return;
            }
            setChartData([]);
            setFilteredChartData([]);
            setIndicators([]);
            setIsDaily(shouldFetchDaily);
            fetchData(shouldFetchDaily);
        }

    }, [props.selectedSecurity, props.selectedBullAndBearItem]);

    useEffect(() => {
        if (chartData.length > 0) {
            try {
                console.log("Filtering chart data for period:", activePeriod);
                const filtered = filterDataByPeriod(activePeriod, chartData);
                setFilteredChartData(filtered);
            } catch (error) {
                console.error("Error filtering data:", error);
                setFilteredChartData(chartData);
            } finally {
                // Set loading to false with a slight delay to ensure UI updates
                setTimeout(() => setIsLoadingTable(false), 300);
            }
        }
    }, [chartData, activePeriod]);

    const getDateFromItem = (item) => {
        if (!item || !item.date) return null;
        const raw = item.date;
        if (typeof raw === 'number' && raw.toString().length === 8) {
            return moment(raw.toString(), 'YYYYMMDD').valueOf();
        }
        return typeof raw === 'number' ? raw : moment(raw).valueOf();
    };

    const filterDataByPeriod = (period, dataArray) => {
        const now = moment();
        let startDate;
        switch (period) {
            case '6M': startDate = now.clone().subtract(6, 'months'); break;
            case '1Y': startDate = now.clone().subtract(1, 'year'); break;
            case '2Y': startDate = now.clone().subtract(2, 'years'); break;
            case '3Y': startDate = now.clone().subtract(3, 'years'); break;
            case '5Y': startDate = now.clone().subtract(5, 'years'); break;
            default: startDate = moment('1900-01-01'); break;
        }

        return dataArray
            .map(item => ({ ...item, date: getDateFromItem(item) }))
            .filter(item => item.date && item.date >= startDate.valueOf())
            .sort((a, b) => a.date - b.date);
    };

    const fetchData = (daily) => {
        if (typeof daily !== 'boolean') return;

        const requestId = ++latestFetchIdRef.current;
        setIsLoadingTable(true);
        setHasError(false);
        setErrorMessage(null);

        technicalAnalysisServiceApi.technicalanalysisFetchDataGet(token, props.entityId, daily)
            .then((response) => {
                try {
                    if (requestId !== latestFetchIdRef.current) return;
                    console.log("API response received:", response.data);
                    const values = response.data?.chartValues || [];
                    const tableValues = response.data?.tableValues || [];

                    // Only show error if both chart values and table values are empty
                    if (tableValues.length === 0 && values.length === 0) {
                        setHasError(true);
                        setErrorMessage(t('technicalAnalysis.noChartData'));
                    } else {
                        // If at least one type of data is available, don't show error
                        setHasError(false);
                        setErrorMessage(null);
                    }

                    // Process table values - use tooltipId to fetch translation text
                    const processedTableValues = tableValues.map(item => {
                        const translationKey = daily
                            ? `TechnicalAnalysisTooltipDaily.${item.tooltipId}`
                            : `TechnicalAnalysisTooltipWeekly.${item.tooltipId}`;

                        console.log('Looking for translation:', translationKey, 'tooltipId:', item.tooltipId);
                        const tooltipText = t(translationKey);
                        console.log('Tooltip text:', tooltipText);

                        return {
                            ...item,
                            info: tooltipText !== translationKey ? tooltipText : item.info || 'Data not available for this indicator.'
                        };
                    });

                    const processed = values
                        .map(item => {
                            const timestamp = dateFormatter.intToTimestampChartFormat(item.date);
                            return {
                                ...item,
                                date: timestamp,
                                signal: item.signal || 'normal'
                            };
                        })
                        .filter(item => item && item.date)
                        .sort((a, b) => a.date - b.date);

                    // Set states in correct order
                    setIndicators(processedTableValues || []);
                    setChartData(processed);

                    setIsLoadingTable(false);
                } catch (error) {
                    if (requestId !== latestFetchIdRef.current) return;
                    console.error("Error processing data:", error);
                    setHasError(true);
                    setErrorMessage(t('technicalAnalysis.unexpectedError'));
                    setIsLoadingTable(false);
                }
            })

            .catch(error => {
                if (requestId !== latestFetchIdRef.current) return;
                console.error("API fetch error:", error);
                setHasError(true);
                setErrorMessage(t('technicalAnalysis.errorFetchingData'));
                setIsLoadingTable(false);
            });
    };

    const clickPeriod = (period) => {
        setActivePeriod(period);
    };

    const handleInfoClick = (indicator, e) => {
        e.stopPropagation(); // Prevent event bubbling
        setSelectedIndicator(indicator);
        setShowInfoModal(true);
    };

    const closeInfoModal = () => {
        setShowInfoModal(false);
        setSelectedIndicator(null);
    };

    return (
        <div className='technical-analysis-sub-screen'>
            <Container>
                <Row>
                    <Col lg='4' className='left-column'>
                        <div className='left-box'>
                            <Container>
                                <Row className='header-row'>
                                    <Col className='text'>{t('technicalAnalysis.indicator')}</Col>
                                    <Col className='text right'><label>{t('technicalAnalysis.indicators')}</label></Col>
                                </Row>
                                {isLoadingTable && <Spinner height='450px' />}
                                {!isLoadingTable && hasError && <div className='error-container'><ErrorWithReload onReload={() => fetchData(isDaily)} height='450px' errorMessage={errorMessage} /></div>}
                                {!isLoadingTable && !hasError && (
                                    <>
                                        {Array.isArray(indicators) && indicators.length > 0 ? (
                                            indicators.map((item, i) => (
                                                <Row key={i} className='content-row'>
                                                    <Col className='text'>
                                                        {item.name}
                                                        <span
                                                            className='info-icon'
                                                            onClick={(e) => handleInfoClick(item, e)}
                                                        >
                                                            i
                                                        </span>
                                                    </Col>
                                                    <Col className='text right'>
                                                        <Image className='bullandbear-image' src={
                                                            item.score === 'vn' ? bearDark :
                                                                item.score === 'n' ? bear :
                                                                    item.score === 'no' ? bullandbear :
                                                                        item.score === 'p' ? bull : bullDark
                                                        } fluid />
                                                    </Col>
                                                </Row>
                                            ))
                                        ) : (
                                            <div className="text-center p-3">{t('technicalAnalysis.noIndicatorData')}</div>
                                        )}
                                    </>
                                )}
                                {!isLoadingTable && Array.isArray(indicators) && indicators.length === 0 && !hasError && (
                                    <div className="text-center p-3">{t('technicalAnalysis.noIndicatorData')}</div>
                                )}
                            </Container>
                        </div>
                    </Col>
                    <Col lg='8' className='right-column'>
                        <div className='right-box'>
                            <div className='period-row p-r-20'>
                                {periods.map(period => (
                                    <div key={period} className={`period-item roboto-medium-14 ${activePeriod === period ? 'active' : ''}`} onClick={() => clickPeriod(period)}>
                                        {period}
                                    </div>
                                ))}
                            </div>
                            <div className='chart-box'>
                                {isLoadingTable && <Spinner height='375px' />}
                                {!isLoadingTable && hasError && <div className='error-container'><ErrorWithReload onReload={() => fetchData(isDaily)} height='375px' errorMessage={errorMessage} /></div>}
                                {!isLoadingTable && !hasError && filteredChartData.length > 0 && (
                                    <LineChartTA
                                        data={filteredChartData}
                                        selectedSecurity={props.selectedSecurity}
                                        key={`chart-${activePeriod}`}
                                    />
                                )}
                                {!isLoadingTable && !hasError && filteredChartData.length === 0 && (
                                    <div className='error-container'>
                                        <div className="text-center p-3">{t('technicalAnalysis.noChartData')}</div>
                                    </div>
                                )}
                            </div>
                            <div className='legend-row transition'>
                                <div><FontAwesomeIcon icon={faHexagonCheck} className='legend-red' /><label className='legend-label'>{t('technicalAnalysis.weak')}</label></div>
                                <div><FontAwesomeIcon icon={faHexagon} className='legend-notr' /><label className='legend-label'>{t('technicalAnalysis.neutral')}</label></div>
                                <div><FontAwesomeIcon icon={faHexagonCheck} className='legend-green' /><label className='legend-label'>{t('technicalAnalysis.strong')}</label></div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Indicator Info Modal */}
            <Modal className='ta-info-modal'
                show={showInfoModal}
                onHide={closeInfoModal}
                size="md"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedIndicator?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='roboto-regular-14 ta-info-text'>
                        {selectedIndicator?.info}
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};






export default TechnicalAnalysis;
